import { useContext, useState } from "react";
import PropTypes from "prop-types";

import { toast } from "react-toastify";

import GuardianEprDao from "../../dao/GuardianEprDao";
import T from "../translations/Translations";

import { GlobalContext } from "../contextProvider/ContextProvider";

/**
 * @name DisplayResources
 * @description Display resources for a given epr
 * @param {[]} resources
 * @returns
 */
const DisplayResources = ({ resources }) => {
    const { state } = useContext(GlobalContext);
    const { token } = state || {};

    const [hasContent, setHasContent] = useState(false);

    const resourcesAssociated = T({ key: "Resources_Associated" });

    /**
     * Use the api to download a file
     * @name fileDownload
     * @param {*} resource
     */
    const fileDownload = (resource) => {
        const { key, uri } = resource;
        let fileName = resource.alias;
        if (uri) {
            const fileNameArr = uri.split("/");
            fileName = fileNameArr.pop();
        }
        const options = {
            action: "eprResourceDownload",
            key,
            token
        };
        GuardianEprDao(options)
            .then((response) => {
                if (response) {
                    const urlCreator = window.URL || window.webkitURL;
                    const url = urlCreator.createObjectURL(new Blob([response.data]));
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", fileName);
                    document.body.appendChild(link);
                    link.click();
                    link.parentNode.removeChild(link);
                    urlCreator.revokeObjectURL(url);
                } else {
                    toast.error("Error. File Not Found!", { autoClose: false });
                }
            })
            .catch((error) => {
                console.error(error);
            });
    };

    /**
     * Download a file or open link in new window
     * @name openExlink
     * @param {{}} resource
     */
    const openExlink = (resource) => {
        if (resource.resourceType === "URL") {
            let newUrl = resource.uri;
            if (newUrl.search(/http/) === -1) {
                newUrl = `http://${resource.uri}`;
            }
            const newWindow = window.open(newUrl, "_blank", "noopener,noreferrer");
            if (newWindow) {
                newWindow.opener = null;
            }
        } else if (resource.resourceType === "FILE_UPLOAD") {
            if (resource.key) {
                fileDownload(resource);
            }
        }
    };

    /** Don't display section unless there are externalResources */
    useState(() => {
        if (resources.length > 0) {
            setHasContent(
                resources.some((resource) => {
                    const { externalResources } = resource;
                    return externalResources.length > 0;
                })
            );
        }
    }, [resources]);

    if (!hasContent) {
        return null;
    }

    return (
        <div className="semester-container">
            <div className="semester-title">{resourcesAssociated}</div>
            <div className="semester-message">
                {resources.map((resource, index) => {
                    const { externalResources, key } = resource;
                    const uniqueResourceKey = `resource-${key}-${index}`;
                    return (
                        <div className="message-container" key={uniqueResourceKey}>
                            {externalResources.map((externalResource, idx) => {
                                const { alias, resourceType } = externalResource;
                                const uniqueExternalResourceKey = `external-resource-${key}-${externalResource.key}-${index}-${idx}`;
                                return (
                                    <div className="resource-row" key={uniqueExternalResourceKey}>
                                        <div
                                            onClick={() => openExlink(externalResource)}
                                            onKeyDown={() => openExlink(externalResource)}
                                            role="button"
                                            tabIndex="0"
                                        >
                                            {resourceType !== "URL" ? (
                                                <div className="truncated">
                                                    <i className="bi bi-file-earmark-arrow-down" /> {alias}
                                                </div>
                                            ) : (
                                                <div className="truncated">
                                                    <i className="bi bi-box-arrow-up-right" /> {alias}
                                                </div>
                                            )}
                                        </div>
                                        {resourceType !== "URL" ? (
                                            <div>
                                                <em>File</em>
                                            </div>
                                        ) : (
                                            <div>
                                                <em>Link</em>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

DisplayResources.propTypes = {
    resources: PropTypes.instanceOf(Array).isRequired
};

export default DisplayResources;
